import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { QueryVariables } from '../../../types';
import { FiltersFormData } from '../../../hooks/use-filters-form';

export interface Badge {
  id: string;
  names: string[];
}

interface IBadgeProps {
  badges: Record<string, Badge>;
  onDismiss: (id: string) => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onClick: () => void;
}

interface UseSearchBadgesReturn {
  badgesProps: IBadgeProps;
}

interface useSearchBadgesParams {
  queryVariables: QueryVariables;
  form: UseFormReturn<FiltersFormData>;
  handleSearch: () => void;
  setIsFiltersOpen: (open: boolean) => void;
  updateUrlParams: (params: Partial<QueryVariables>) => void;
}

const useSearchBadges = ({ queryVariables, form, handleSearch, setIsFiltersOpen, updateUrlParams }: useSearchBadgesParams): UseSearchBadgesReturn => {
  const badges = useMemo(() => {
    if (!queryVariables) return {};

    return Object.entries(queryVariables)
      .filter(([, value]) => (Array.isArray(value) ? value.length > 0 : value !== undefined && value !== ''))
      .reduce((acc, [key, value]) => {
        acc[key] = {
          id: key,
          names: Array.isArray(value) ? value.map(String) : [String(value)],
        };
        return acc;
      }, {} as Record<string, { id: string; names: string[] }>);
  }, [queryVariables]);

  const handleDismiss = (id: string) => async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();

    const newQueryVariables = { ...queryVariables };
    newQueryVariables[id] = [];

    form.setValue(id as keyof FiltersFormData, []);

    await updateUrlParams(newQueryVariables);
    handleSearch();
  };

  const handleOpenFilters = () => {
    setIsFiltersOpen(true);
  };

  return {
    badgesProps: {
      badges,
      onClick: handleOpenFilters,
      onDismiss: handleDismiss,
    },
  };
};

export default useSearchBadges;
